import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Words, FadeUp, PopIn, EASE } from "@/components/Reveal";
import PillButton from "@/components/PillButton";
import EpisodeCard from "@/components/EpisodeCard";
import { CAUSES, getCause } from "@/data/causes";
import { getEpisode } from "@/data/episodes";
import NotFound from "@/pages/NotFound";

const CauseDetail = () => {
  const { slug } = useParams();
  const cause = getCause(slug);
  if (!cause) return <NotFound />;

  const relatedEpisodes = cause.related.map((rid) => getEpisode(rid)).filter(Boolean);
  const keepReading = cause.keepReading.map((s) => CAUSES.find((c) => c.slug === s)).filter(Boolean);

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="w-full bg-white pt-[110px] sm:pt-[130px] lg:pt-[150px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto flex max-w-[1208px] flex-col gap-5 lg:px-[60px]">
            <FadeUp mode="load" y={12}>
              <Link to="/causes" data-testid="cause-back-link" className="group/link inline-flex items-center gap-1 font-display text-[13px] uppercase tracking-[0.18em] text-charcoal transition-colors hover:text-black">
                <span aria-hidden="true" className="transition-transform duration-500 group-hover/link:-translate-x-1">←</span> All issues
              </Link>
            </FadeUp>
            <PopIn mode="load">
              <span className="inline-flex w-fit items-center rounded-full border border-silver px-4 py-[3px] text-[13px] uppercase leading-[22.4px] tracking-[0.08em] text-charcoal font-display">
                {cause.tag}
              </span>
            </PopIn>
            <h1 className="max-w-[900px] font-display text-[36px] leading-[1.08] text-black sm:text-[48px] lg:text-[56px] lg:leading-[62px]" aria-label={cause.headline}>
              <Words text={cause.headline} mode="load" delay={0.25} stagger={0.055} />
            </h1>
            <FadeUp mode="load" delay={0.55}>
              <p className="max-w-[640px] text-[15px] leading-[26px] text-charcoal sm:text-[15.6px] sm:leading-[27.2px]">{cause.description}</p>
            </FadeUp>
            <FadeUp mode="load" delay={0.65}>
              <div className="mt-2">
                <PillButton href="/community" dataTestId="cause-join-button">
                  Join the Community
                </PillButton>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <section className="w-full bg-white pt-12 pb-4 sm:pt-16">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <motion.div
            initial={{ clipPath: "inset(0% 0% 0% 100%)" }}
            whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1.1, ease: EASE }}
            className="group relative mx-auto aspect-[1208/560] max-h-[560px] w-full max-w-[1208px] overflow-hidden rounded-3xl"
          >
            <div className="absolute inset-0 transition-transform duration-[1400ms] group-hover:scale-[1.04]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
              <img alt={cause.imageAlt} className="h-full w-full object-cover" src={cause.image} />
            </div>
            <div className="pointer-events-none absolute inset-0 bg-linen opacity-10 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-0" />
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="w-full bg-white py-14 sm:py-16 lg:py-[72px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto flex max-w-[1208px] flex-col gap-16 lg:px-[60px]">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
              <div className="flex flex-col gap-4">
                <FadeUp y={14}>
                  <span className="eyebrow block text-[12px] leading-[20px] text-charcoal-mid">Overview</span>
                </FadeUp>
                <FadeUp y={18}>
                  <p className="max-w-[560px] text-[16px] leading-[28px] text-charcoal-dark sm:text-[17px] sm:leading-[30px]">{cause.overview}</p>
                </FadeUp>
              </div>
              <div className="flex flex-col gap-4">
                <FadeUp y={14}>
                  <span className="eyebrow block text-[12px] leading-[20px] text-charcoal-mid">Why it matters</span>
                </FadeUp>
                <div className="flex flex-col gap-3">
                  {cause.whyItMatters.map((w, i) => (
                    <FadeUp key={w} delay={0.06 * i} y={14}>
                      <div className="flex items-start gap-3">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-silver" aria-hidden="true" />
                        <p className="text-[14.6px] leading-[25px] text-charcoal">{w}</p>
                      </div>
                    </FadeUp>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
              <div className="flex flex-col gap-5">
                <FadeUp y={14}>
                  <span className="eyebrow block text-[12px] leading-[20px] text-charcoal-mid">Current Challenges</span>
                </FadeUp>
                <h2 className="font-display text-[26px] leading-[1.15] text-black sm:text-[30px]">
                  <Words text="Where the work gets hard" />
                </h2>
                <div className="flex flex-col divide-y divide-silver-line">
                  {cause.challenges.map((c, i) => (
                    <FadeUp key={c} delay={0.06 * i} y={14}>
                      <div className="flex items-start gap-5 py-4">
                        <span className="font-display text-[13px] uppercase tracking-[0.22em] text-charcoal-mid">0{i + 1}</span>
                        <p className="text-[14.6px] leading-[25px] text-charcoal-dark">{c}</p>
                      </div>
                    </FadeUp>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-5">
                <FadeUp y={14}>
                  <span className="eyebrow block text-[12px] leading-[20px] text-charcoal-mid">Proposed Direction</span>
                </FadeUp>
                <h2 className="font-display text-[26px] leading-[1.15] text-black sm:text-[30px]">
                  <Words text="How the show contributes" />
                </h2>
                <div className="flex flex-col gap-3">
                  {cause.direction.map((d, i) => (
                    <FadeUp key={d} delay={0.06 * i} y={14}>
                      <div className="flex items-start gap-3">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-silver" aria-hidden="true" />
                        <p className="text-[14.6px] leading-[25px] text-charcoal">{d}</p>
                      </div>
                    </FadeUp>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <FadeUp y={14}>
                <h2 className="font-display text-[26px] leading-[1.15] text-black sm:text-[30px]">
                  <Words text="Partner Organizations" />
                </h2>
              </FadeUp>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {cause.partners.map((p, i) => (
                  <FadeUp key={p.name} delay={0.05 * i} y={14} className="h-full">
                    <div data-testid={`cause-partner-${i + 1}`} className="h-full rounded-2xl border border-hairline bg-white-smoke p-6 transition-all duration-500 hover:-translate-y-0.5 hover:border-silver hover:bg-white">
                      <p className="font-display text-[18px] leading-[26px] text-black">{p.name}</p>
                      <p className="mt-2 text-[13.6px] leading-[23px] text-charcoal">{p.description}</p>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-8">
              <FadeUp y={14}>
                <h2 className="font-display text-[26px] leading-[1.15] text-black sm:text-[30px]">
                  <Words text="Related Episodes" />
                </h2>
              </FadeUp>
              <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
                {relatedEpisodes.map((rel, i) => (
                  <EpisodeCard key={rel.id} episode={rel} index={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Keep reading */}
      <section className="w-full bg-white-smoke py-16 sm:py-20 lg:py-[88px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto flex max-w-[1208px] flex-col gap-10 lg:px-[60px]">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
              <div className="flex flex-col gap-2">
                <FadeUp y={12}>
                  <span className="eyebrow block text-[12px] leading-[20px] text-charcoal-mid">Keep reading</span>
                </FadeUp>
                <h2 className="font-display text-[28px] leading-[1.15] text-black sm:text-[34px] lg:text-[40px]">
                  <Words text="More issues she comes back to" />
                </h2>
              </div>
              <FadeUp y={10}>
                <Link to="/causes" data-testid="cause-keep-reading-all-link" className="group/link inline-flex items-center font-display text-[15px] leading-[25.6px] text-charcoal sm:text-[16px]">
                  <span className="transition-transform duration-500 ease-out group-hover/link:-translate-x-1">All issues</span>
                  <span aria-hidden="true" className="ml-1 transition-transform duration-500 ease-out group-hover/link:translate-x-1">→</span>
                </Link>
              </FadeUp>
            </div>
            <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
              {keepReading.map((c, i) => (
                <FadeUp key={c.slug} delay={0.06 * i} y={20} className="h-full">
                  <Link
                    to={`/causes/${c.slug}`}
                    data-testid={`cause-keep-reading-${c.slug}`}
                    className="group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-hairline bg-white transition-all duration-500 hover:-translate-y-1 hover:border-silver hover:shadow-[0_24px_60px_-32px_rgba(78,81,85,0.35)]"
                  >
                    <div className="relative h-[194px] w-full overflow-hidden">
                      <div className="absolute inset-0 transition-transform duration-[900ms] group-hover:scale-[1.07]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
                        <img alt={c.imageAlt} className="h-full w-full object-cover" src={c.image} />
                      </div>
                      <span className="absolute right-3 top-3 rounded-full bg-white/95 px-3 py-1 text-[11px] uppercase leading-[16px] tracking-[0.14em] text-charcoal font-display">
                        {c.tag}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col px-6 py-7">
                      <h3 className="font-display text-[22px] leading-[1.25] text-black sm:text-[24px]">{c.headline}</h3>
                      <span className="mt-auto pt-4 font-display text-[14px] leading-[22.4px] text-charcoal underline decoration-silver underline-offset-4 transition-colors group-hover:text-black group-hover:decoration-black">
                        Open issue
                      </span>
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
            <div className="flex flex-col items-start gap-6 rounded-3xl bg-linen p-8 sm:p-12 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex max-w-[560px] flex-col gap-3">
                <h3 className="font-display text-[24px] leading-[1.2] text-black sm:text-[30px]">
                  <Words text="Stand alongside this work" />
                </h3>
                <FadeUp y={14}>
                  <p className="text-[14.6px] leading-[26px] text-charcoal-dark sm:text-[15.2px]">
                    Join Stephanie’s community for updates on the partners in this issue and how a small step from you would help.
                  </p>
                </FadeUp>
              </div>
              <FadeUp y={12}>
                <PillButton href="/community" dataTestId="cause-bottom-join-button">
                  Join the Community
                </PillButton>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CauseDetail;
